import { useState } from "react";
import {
  Button,
  ButtonGroup,
  Container,
  Spinner,
  Table,
} from "react-bootstrap";
import { Title } from "../components/common/Title";
import { useApiResource } from "../hooks/useApiResource";
import type { FernUsage as FernUsageData } from "../types/fernQueries";

const API_HOST = process.env.REACT_APP_API;

const PERIODS = [
  { label: "Today", days: 1 },
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
  { label: "All time", days: null },
] as const;

const formatTime = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

export const FernUsage = () => {
  const [days, setDays] = useState<number | null>(1);
  const query = days === null ? "" : `?days=${days}`;
  const { data, loading, error, reload } = useApiResource<FernUsageData>(
    `${API_HOST}public/fern-usage${query}`,
    `fern-usage-${days ?? "all"}`,
  );

  return (
    <Container fluid>
      <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
        <Title text="Fern Usage" />
        <div className="d-flex align-items-center gap-2">
          <ButtonGroup size="sm" aria-label="Usage period">
            {PERIODS.map((period) => (
              <Button
                key={period.label}
                variant={days === period.days ? "warning" : "outline-light"}
                onClick={() => setDays(period.days)}
              >
                {period.label}
              </Button>
            ))}
          </ButtonGroup>
          <Button
            variant="outline-light"
            size="sm"
            onClick={reload}
            disabled={loading}
          >
            Refresh
          </Button>
        </div>
      </div>

      {loading && !data && <Spinner animation="border" variant="warning" />}
      {error && <div className="text-danger">{error.message}</div>}

      {data && (
        <>
          <p className="text-white mb-2">
            <strong>{data.totalRequests}</strong> requests from{" "}
            <strong>{data.uniqueSenders}</strong> named requesters.
          </p>
          <Table
            bordered
            hover
            variant="dark"
            size="sm"
            responsive
            className="fern-log"
          >
            <thead>
              <tr>
                <th>Requester</th>
                <th>Requests</th>
                <th>Last used</th>
              </tr>
            </thead>
            <tbody>
              {data.bySender.map((row) => (
                <tr key={row.sender ?? "unknown"}>
                  <td>{row.sender ?? "Unknown"}</td>
                  <td>{row.requests}</td>
                  <td className="text-nowrap">{formatTime(row.lastUsedAt)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}
    </Container>
  );
};

export default FernUsage;
